import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import SearchIcon from '@material-ui/icons/Search';
import EntityListItem from 'components/EntitySelectDialog/components/EntityListItem';
import Button from 'components/StyledButton/StyledButton';
import moment from 'moment';
import { useDebounce } from 'use-debounce';
import uuid4 from 'uuid4';

import GreenButton from '../../common/styled/GreenButton/GreenButton';
import CustomLoader from '../../components/CustomLoader/CustomLoader';
import CustomTablePagination from '../../components/CustomTablePagination/CustomTablePagination';
import CompanyDetailsDialog from '../../dialogs/CompanyDetailsDialog/CompanyDetailsDialog';
import { ReduxState } from '../../redux/types';
import Company from '../../types/Company';
import api from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import snackbar from '../../utils/snackbar';
import { ActionTableCell, FlexContainer, Title } from './styled';

const CompaniesPage = () => {
    const { t } = useTranslation();

    const isAdmin = useSelector((state: ReduxState) => state.user.type) === 'admin';
    const isCertifier = useSelector((state: ReduxState) => state.user.type) === 'certifier';

    const { hash } = useLocation();

    const [isLoading, setIsLoading] = useState(true);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [selectedCompanyId, selectCompanyId] = useState(0);
    const [isNewCompany, setIsNewCompany] = useState(false);

    const [paginationPage, setPaginationPage] = useState(0);
    const [paginationTotal, setPaginationTotal] = useState(0);
    const [paginationItemsPerPage, setPaginationItemsPerPage] = useState(25);

    const [query, setQuery] = useState('');
    const [loadedQuery, setLoadedQuery] = useState('');
    const [loadedPage, setLoadedPage] = useState(0);
    const [loadedItemsPerPage, setLoadedItemsPerPage] = useState(25);
    const [internalLoading, setInternalLoading] = useState(false);
    const [debouncedQuery] = useDebounce(query, 500);

    const [isCompanyDialogOpen, setCompanyDialogOpen] = useState(false);

    const [openDialog, closeDialog] = useDialog();

    const apiRequestId = useRef('');

    useEffect(() => {
        setInternalLoading(query !== loadedQuery || paginationPage !== loadedPage || paginationItemsPerPage !== loadedItemsPerPage);
    }, [query, loadedQuery, paginationPage, loadedPage, paginationItemsPerPage, loadedItemsPerPage]);

    const getData = useCallback(() => {
        const currentRequestId = uuid4();
        apiRequestId.current = currentRequestId;

        api.request('/companies', 'GET', { query: debouncedQuery, page: paginationPage + 1, perPage: paginationItemsPerPage }).then((res) => {
            if (currentRequestId !== apiRequestId.current) return;

            const { data, total, lastPage } = res;
            setCompanies(data);
            setPaginationTotal(total);
            setLoadedQuery(debouncedQuery);
            setLoadedPage(paginationPage);
            setLoadedItemsPerPage(paginationItemsPerPage);

            if (paginationPage + 1 > lastPage) {
                setPaginationPage(lastPage - 1);
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }, [debouncedQuery, paginationPage, paginationItemsPerPage]);

    const openCompanyDialog = useCallback((companyId: number) => {
        setIsNewCompany(companyId === 0);
        selectCompanyId(companyId);
        setCompanyDialogOpen(true);
    }, []);

    const closeCompanyDialog = useCallback((companyId?: number) => {
        if (companyId) {
            selectCompanyId(companyId);
        }
        setCompanyDialogOpen(false);
    }, [setCompanyDialogOpen]);

    const deleteCompany = useCallback((companyId: number) => {
        api.request('/companies/' + companyId, 'DELETE').then(() => {
            snackbar.success(t('dialogs.company_delete.success'));
            getData();
        });
    }, [getData, t]);

    const deleteCompanyDialog = useCallback((companyId: number) => {
        openDialog({
            title: t('dialogs.company_delete.title'),
            message: t('dialogs.company_delete.message'),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteCompany(companyId);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteCompany, openDialog, t]);

    useEffect(() => {
        if (!isCompanyDialogOpen) getData();
    }, [getData, isCompanyDialogOpen]);

    useEffect(() => {
        setPaginationPage(0);
    }, [paginationItemsPerPage]);

    useEffect(() => {
        if (hash) {
            openCompanyDialog(parseInt(hash.replace('#', ''), 10));
            window.history.replaceState(null, '', ' ');
        }
    }, [hash, openCompanyDialog]);

    const disableEnableCompany = useCallback((userId: number, enabled: boolean) => {
        api.request('/companies/' + userId + '/' + (enabled ? 'enable' : 'disable'), 'POST').then(() => {
            snackbar.success(t('pages.companies.' + (enabled ? 'company_enabled_success' : 'company_disabled_success')));
            getData();
        });
    }, [getData, t]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '100%', maxHeight: '100%', height: '100%' }}>
                <FlexContainer>
                    <Title variant='h4' style={{ flex: 0 }}>{t('pages.companies.title')}</Title>

                    <div style={{ flex: 1, flexGrow: 1, marginLeft: '24px', display: 'flex' }}>
                        <TextField
                            label='Ricerca cliente'
                            variant='outlined'
                            placeholder='Ricerca per nome'
                            value={query}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position='start'><SearchIcon /></InputAdornment>,
                            }}
                            style={{ width: '100%', maxWidth: '600px' }}
                            size='small'
                        />

                        {internalLoading && (
                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '16px' }}>
                                <CircularProgress />
                            </div>
                        )}
                    </div>

                    {(isAdmin || isCertifier) && <GreenButton variant='outlined' onClick={() => openCompanyDialog(0)}>
                        {t('pages.companies.new_customer')}
                    </GreenButton>}
                </FlexContainer>

                <div style={{ display: 'flex' }}>

                </div>

                <TableContainer component={Paper} style={{ marginTop: '20px' }} className='book-list-table-view'>
                    <Table aria-label='simple table'>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('pages.companies.table.name')}</TableCell>
                                <TableCell>{t('pages.companies.table.state')}</TableCell>
                                <TableCell>{t('pages.companies.table.last_update_date')}</TableCell>
                                <TableCell>{t('pages.companies.table.user')}</TableCell>

                                {!isCertifier && (
                                    <TableCell>{t('pages.companies.table.certifier')}</TableCell>
                                )}

                                <TableCell>{t('pages.companies.table.auditor')}</TableCell>
                                <ActionTableCell>{t('pages.companies.table.actions')}</ActionTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {companies.map(company => {
                                const style = { backgroundColor: company.enabled ? undefined : '#fdecea' };

                                return (
                                    <TableRow key={company.id}>
                                        <TableCell style={style}>{company.name}</TableCell>
                                        <TableCell style={style}>{t('dialogs.company_details.states.' + company.state)}</TableCell>
                                        <TableCell style={style}>{company.lastUpdateDate ? moment(company.lastUpdateDate).format('DD/MM/YYYY') : 'n.a.'}</TableCell>
                                        <TableCell style={{ padding: 0 }}>
                                            <List style={{ padding: 0 }}>
                                                {company.users.length === 0 && (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <Chip label={'Utente non associato'} variant={'default'} style={{ backgroundColor: '#fdecea', border: '1px solid #f44336' }} /><br />
                                                    </div>
                                                )}
                                                {company.users.map((user: any) => {
                                                    return (
                                                        <EntityListItem
                                                            name={user.surname + ' ' + user.name}
                                                            description={user.email}
                                                        />
                                                    );
                                                })}
                                            </List>
                                        </TableCell>
                                        {!isCertifier && (
                                            <TableCell style={{ padding: 0 }}>
                                                <List style={{ padding: 0 }}>
                                                    {company.certifiers.length === 0 && (
                                                        <div style={{ textAlign: 'center' }}>
                                                            <Chip label={'Certifier non associato'} variant={'default'} style={{ backgroundColor: '#fdecea', border: '1px solid #f44336' }} /><br />
                                                        </div>
                                                    )}
                                                    {company.certifiers.map((user: any) => {
                                                        return (
                                                            <EntityListItem
                                                                name={user.surname + ' ' + user.name}
                                                                description={user.email}
                                                            />
                                                        );
                                                    })}
                                                </List>
                                            </TableCell>
                                        )}
                                        <TableCell style={{ padding: 0 }}>
                                            <List style={{ padding: 0 }}>
                                                {company.auditors.length === 0 && (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <Chip label={'Auditor non associato'} variant={'default'} style={{ backgroundColor: '#fdecea', border: '1px solid #f44336' }} /><br />
                                                    </div>
                                                )}
                                                {company.auditors.map((user: any) => {
                                                    return (
                                                        <EntityListItem
                                                            name={user.surname + ' ' + user.name}
                                                            description={user.email}
                                                        />
                                                    );
                                                })}
                                            </List>
                                        </TableCell>
                                        <ActionTableCell style={style}>
                                            <Button variant='outlined' color='primary' onClick={() => openCompanyDialog(company.id)}>
                                                {t('pages.companies.table.details')}
                                            </Button>
                                            {(isAdmin || isCertifier) && (
                                                <>
                                                    {' '}
                                                    <Button variant='outlined' color={company.enabled ? 'secondary' : 'primary'} onClick={() => disableEnableCompany(company.id, !company.enabled)}>
                                                        {t('pages.companies.table.' + (company.enabled ? 'disable' : 'enable'))}
                                                    </Button>
                                                    {isAdmin && (
                                                        <>
                                                            {' '}
                                                            <Button variant='outlined' color='secondary' onClick={() => deleteCompanyDialog(company.id)}>
                                                                {t('pages.generic.table.delete')}
                                                            </Button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </ActionTableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <CustomTablePagination
                    count={paginationTotal}
                    page={paginationPage}
                    onPageChange={(_e, page) => setPaginationPage(page)}
                    rowsPerPage={paginationItemsPerPage}
                    onRowsPerPageChange={(e) => setPaginationItemsPerPage(parseInt(e.target.value, 10))}
                    style={{ flexShrink: '0', marginBottom: '-24px' }}
                />

            </div >

            <CompanyDetailsDialog open={isCompanyDialogOpen} onClose={closeCompanyDialog} companyId={selectedCompanyId} isNewCompany={isNewCompany} />
        </>
    );
};

export default CompaniesPage;
